import DateTime from '../datetime';

/**
 * Formats a date time into a short date string representation.
 * @param dt - The date.
 * @returns The formatted string. Example: "Jan 2, 2024"
 */
export const formatDateShort = (dt: Date): string => {
  return DateTime(dt).format('MMM DD, YYYY');
};

/**
 * Formats a date into a time string representation.
 * @param dt - The date.
 * @returns The formatted string. Example: "10:30pm"
 */
export const formatTime = (dt: Date): string => {
  return DateTime(dt).format('h:mma');
};

/**
 * Formats a date time into a full date string representation.
 * @param dt - The date.
 * @returns The formatted string. Example: "Jan 2, 2024 10:30pm"
 */
export const formatDateTimeShort = (dt: Date): string => {
  return `${formatDateShort(dt)} ${formatTime(dt)}`;
};

/**
 * Formats a date time into a localized full date string representation.
 * @param date - The date.
 * @param timeZone - The timeZone, defaults to local.
 * @param locale - The locale, defaults to local.
 * @param shortYear - Whether the year is YY or YYYY
 * @returns The formatted string. Example: "04/06/98 9:00 AM"
 */
export function formatDateToLocale(
  date: Date,
  timeZone?: string,
  locale?: string,
  shortYear: boolean = true,
): string {
  return date
    .toLocaleString(locale, {
      year: shortYear ? '2-digit' : 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone,
    })
    .replace(',', '');
}

export function formatDateToLocaleTimeAndDate(date: Date) {
  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const dateString = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  return { timeString, dateString };
}
